/**
 * Shaffof ID — brauzer SDK.
 *
 * Markaz platformalari uchun yagona avtorizatsiya. Standart OpenID Connect
 * Authorization Code + PKCE oqimini amalga oshiradi, shuning uchun
 * client_secret brauzerga chiqmaydi.
 *
 *   const shaffof = new ShaffofId({
 *     issuer: 'https://id.shaffof.uz',
 *     clientId: '...',
 *     redirectUri: 'https://portal.example.uz/callback',
 *   })
 *
 *   loginButton.onclick = () => shaffof.login()
 *   // callback sahifasida:
 *   const { accessToken, user } = await shaffof.handleCallback()
 */

export type ShaffofIdOptions = {
  /** Shaffof ID serveri manzili, masalan https://id.shaffof.uz */
  issuer: string
  /** Superadmin panelidan olingan client_id */
  clientId: string
  /** Ro'yxatdan o'tkazilgan redirect URI */
  redirectUri: string
  /** So'raladigan ma'lumotlar. Standart: 'openid profile email' */
  scope?: string
  /** Chiqishdan keyin qaytish manzili */
  postLogoutRedirectUri?: string
  /** Holatni saqlash joyi. Standart: sessionStorage */
  storage?: Storage
  /**
   * Confidential mijozlarda token almashinuvi backendda bo'ladi.
   * Bu manzilga `{ code, code_verifier, state }` POST qilinadi.
   * Ko'rsatilsa, SDK to'g'ridan-to'g'ri /oauth/token ga murojaat qilmaydi.
   */
  tokenExchangeUrl?: string
  /** OIDC prompt parametri: 'login' | 'consent' | 'none' */
  prompt?: string
  /** Interfeys tili */
  uiLocales?: 'uz' | 'ru' | 'en'
}

import type { SignOptions, SignResult } from './sign'
import { openSigningFrame } from './sign'

export type TokenSet = {
  accessToken: string
  idToken: string | null
  refreshToken: string | null
  tokenType: string
  expiresIn: number
  expiresAt: number
  scope: string[]
}

export type ShaffofUser = {
  sub: string
  name?: string
  given_name?: string
  family_name?: string
  middle_name?: string
  preferred_username?: string
  email?: string
  email_verified?: boolean
  phone_number?: string
  pinfl?: string
  roles?: string[]
  organization?: { tin?: string; name?: string; position?: string }
  eimzo?: { serial_number: string; common_name: string | null; valid_to: string | null }
  /** Shu seansda qaysi sifatda kirgan: o'z nomidan yoki tashkilot nomidan */
  subject_type?: 'individual' | 'legal'
  /** Qaysi usul bilan kirgan: oneid | eimzo | qr | password */
  amr?: string[]
  /** Qachon kirgan (Unix vaqti) — «yaqinda kirganmi» tekshiruvi uchun */
  auth_time?: number
  [claim: string]: unknown
}

export type CallbackResult = TokenSet & { user: ShaffofUser }

export class ShaffofIdError extends Error {
  code: string
  description: string | undefined

  constructor(code: string, message: string, description?: string) {
    super(message)
    this.name = 'ShaffofIdError'
    this.code = code
    this.description = description
  }
}

const STATE_KEY = 'shaffof-id:state'

export class ShaffofId {
  private readonly options: Required<Pick<ShaffofIdOptions, 'issuer' | 'clientId' | 'redirectUri' | 'scope'>> &
    ShaffofIdOptions

  private discovery: Promise<DiscoveryDocument> | null = null

  constructor(options: ShaffofIdOptions) {
    if (!options.issuer) throw new ShaffofIdError('config', 'issuer ko\'rsatilmagan')
    if (!options.clientId) throw new ShaffofIdError('config', 'clientId ko\'rsatilmagan')
    if (!options.redirectUri) throw new ShaffofIdError('config', 'redirectUri ko\'rsatilmagan')

    this.options = {
      ...options,
      issuer: options.issuer.replace(/\/$/, ''),
      scope: options.scope ?? 'openid profile email',
    }
  }

  private get storage(): Storage {
    return this.options.storage ?? sessionStorage
  }

  /** Foydalanuvchini Shaffof ID login sahifasiga yuboradi */
  async login(overrides: { prompt?: string; loginHint?: string } = {}): Promise<void> {
    const url = await this.buildAuthorizationUrl(overrides)
    window.location.assign(url)
  }

  /** Login URL'ini qaytaradi (masalan, `<a href>` uchun) */
  async buildAuthorizationUrl(overrides: { prompt?: string; loginHint?: string } = {}): Promise<string> {
    const { authorization_endpoint } = await this.getDiscovery()

    const verifier = randomString(64)
    const state = randomString(32)
    const nonce = randomString(32)

    this.storage.setItem(STATE_KEY, JSON.stringify({ verifier, state, nonce, at: Date.now() }))

    const params = new URLSearchParams({
      client_id: this.options.clientId,
      redirect_uri: this.options.redirectUri,
      response_type: 'code',
      scope: this.options.scope,
      state,
      nonce,
      code_challenge: await pkceChallenge(verifier),
      code_challenge_method: 'S256',
    })

    const prompt = overrides.prompt ?? this.options.prompt
    if (prompt) params.set('prompt', prompt)
    if (overrides.loginHint) params.set('login_hint', overrides.loginHint)
    if (this.options.uiLocales) params.set('ui_locales', this.options.uiLocales)

    return `${authorization_endpoint}?${params}`
  }

  /**
   * Callback sahifasida chaqiriladi: URL'dagi `code` ni tokenga almashtiradi
   * va foydalanuvchi ma'lumotlarini oladi.
   */
  async handleCallback(url: string = window.location.href): Promise<CallbackResult> {
    const params = new URL(url).searchParams

    const error = params.get('error')
    if (error) {
      throw new ShaffofIdError(error, errorMessage(error), params.get('error_description') ?? undefined)
    }

    const code = params.get('code')
    if (!code) throw new ShaffofIdError('missing_code', 'URL manzilida `code` topilmadi.')

    const saved = this.storage.getItem(STATE_KEY)
    if (!saved) {
      throw new ShaffofIdError('missing_state', 'Kirish seansi topilmadi. Qaytadan urinib ko\'ring.')
    }

    const { verifier, state, nonce } = JSON.parse(saved) as {
      verifier: string; state: string; nonce: string
    }

    if (params.get('state') !== state) {
      throw new ShaffofIdError('state_mismatch', 'State mos kelmadi — so\'rov buzilgan bo\'lishi mumkin.')
    }

    this.storage.removeItem(STATE_KEY)

    const tokens = await this.exchangeCode(code, verifier, state)

    if (tokens.idToken) {
      const claims = decodeJwt(tokens.idToken)
      if (claims.nonce && claims.nonce !== nonce) {
        throw new ShaffofIdError('nonce_mismatch', 'Nonce mos kelmadi.')
      }
    }

    const user = await this.fetchUserInfo(tokens.accessToken)

    return { ...tokens, user }
  }

  /** access_token orqali foydalanuvchi ma'lumotlari */
  async fetchUserInfo(accessToken: string): Promise<ShaffofUser> {
    const { userinfo_endpoint } = await this.getDiscovery()

    const response = await fetch(userinfo_endpoint, {
      headers: { Authorization: `Bearer ${accessToken}`, Accept: 'application/json' },
    })

    if (!response.ok) {
      throw new ShaffofIdError('userinfo_failed', 'Foydalanuvchi ma\'lumotlarini olib bo\'lmadi.')
    }

    return (await response.json()) as ShaffofUser
  }

  /** Refresh token orqali yangi access_token */
  async refresh(refreshToken: string): Promise<TokenSet> {
    const { token_endpoint } = await this.getDiscovery()

    return this.postTokenRequest(token_endpoint, {
      grant_type: 'refresh_token',
      client_id: this.options.clientId,
      refresh_token: refreshToken,
    })
  }

  /** Shaffof ID sessiyasini ham yopib chiqish */
  async logout(idToken?: string): Promise<void> {
    const { end_session_endpoint } = await this.getDiscovery()

    const params = new URLSearchParams({ client_id: this.options.clientId })
    if (idToken) params.set('id_token_hint', idToken)
    if (this.options.postLogoutRedirectUri) {
      params.set('post_logout_redirect_uri', this.options.postLogoutRedirectUri)
    }

    this.storage.removeItem(STATE_KEY)
    window.location.assign(`${end_session_endpoint}?${params}`)
  }

  /**
   * Hujjatni o'z sahifangizda imzolatish.
   *
   * Imzolash oynasi Shaffof ID dan keladi (iframe) — E-IMZO bilan
   * ishlash, apparat tokenlar va yuzni tasdiqlash o'sha yerda qoladi,
   * foydalanuvchi esa sizning sahifangizdan chiqmaydi.
   *
   *   const result = await shaffof.signDocument('9f1c2a4e-...')
   *   if (result.status === 'signed') { ... }
   *
   * Hujjat avval server tomonda yaratilgan bo'lishi kerak
   * (`POST /api/documents`), uning `uuid` si shu yerga beriladi.
   *
   * Sahifangiz manzili hujjatni yuborgan tizimning ro'yxatdan o'tgan
   * `redirect_uris` i bilan mos kelishi shart — aks holda Shaffof ID
   * oynani ramkada ochishga ruxsat bermaydi.
   */
  signDocument(uuid: string, options: SignOptions = {}): Promise<SignResult> {
    return openSigningFrame(this.options.issuer, uuid, options)
  }

  /** OIDC discovery hujjati (bir marta yuklanadi) */
  async getDiscovery(): Promise<DiscoveryDocument> {
    this.discovery ??= fetch(`${this.options.issuer}/.well-known/openid-configuration`)
      .then((response) => {
        if (!response.ok) {
          throw new ShaffofIdError('discovery_failed', 'Shaffof ID serveriga ulanib bo\'lmadi.')
        }
        return response.json() as Promise<DiscoveryDocument>
      })
      .catch((e) => {
        this.discovery = null
        throw e
      })

    return this.discovery
  }

  private async exchangeCode(code: string, verifier: string, state: string): Promise<TokenSet> {
    // Confidential mijozlarda almashinuv backendda — secret brauzerga chiqmaydi
    if (this.options.tokenExchangeUrl) {
      const response = await fetch(this.options.tokenExchangeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code, code_verifier: verifier, state }),
      })

      const body = await response.json()

      if (!response.ok) {
        throw new ShaffofIdError(body.error ?? 'exchange_failed', body.message ?? 'Tokenni olib bo\'lmadi.')
      }

      return normalizeTokens(body)
    }

    const { token_endpoint } = await this.getDiscovery()

    return this.postTokenRequest(token_endpoint, {
      grant_type: 'authorization_code',
      client_id: this.options.clientId,
      redirect_uri: this.options.redirectUri,
      code,
      code_verifier: verifier,
    })
  }

  private async postTokenRequest(endpoint: string, body: Record<string, string>): Promise<TokenSet> {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
      body: new URLSearchParams(body),
    })

    const data = await response.json().catch(() => ({}))

    if (!response.ok) {
      throw new ShaffofIdError(
        data.error ?? 'token_failed',
        errorMessage(data.error ?? 'token_failed'),
        data.error_description ?? data.message,
      )
    }

    return normalizeTokens(data)
  }
}

/* ------------------------------------------------------------- Yordamchi */

type DiscoveryDocument = {
  issuer: string
  authorization_endpoint: string
  token_endpoint: string
  userinfo_endpoint: string
  jwks_uri: string
  end_session_endpoint: string
  introspection_endpoint?: string
  revocation_endpoint?: string
}

function normalizeTokens(data: Record<string, unknown>): TokenSet {
  const expiresIn = Number(data.expires_in ?? 3600)

  return {
    accessToken: String(data.access_token),
    idToken: (data.id_token as string) ?? null,
    refreshToken: (data.refresh_token as string) ?? null,
    tokenType: String(data.token_type ?? 'Bearer'),
    expiresIn,
    expiresAt: Date.now() + expiresIn * 1000,
    scope: String(data.scope ?? '').split(' ').filter(Boolean),
  }
}

/** JWT'ni imzo tekshirmasdan o'qiydi — faqat nonce solishtirish uchun */
export function decodeJwt(token: string): Record<string, string | undefined> {
  const payload = token.split('.')[1]
  if (!payload) return {}

  const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))

  return JSON.parse(decodeURIComponent(escape(json)))
}

function randomString(length: number): string {
  const bytes = new Uint8Array(length)
  crypto.getRandomValues(bytes)

  return Array.from(bytes, (b) => 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~'[b % 64]).join('')
}

async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))

  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

const MESSAGES: Record<string, string> = {
  access_denied: 'Kirish rad etildi.',
  consent_required: 'Ma\'lumotlaringizdan foydalanishga rozilik kerak.',
  login_required: 'Avval tizimga kirishingiz kerak.',
  invalid_scope: 'So\'ralgan ma\'lumotlarga ruxsat berilmagan.',
  unauthorized_client: 'Bu proyektga ruxsat berilmagan yoki to\'xtatilgan.',
  invalid_client: 'client_id yoki client_secret noto\'g\'ri.',
  invalid_grant: 'Avtorizatsiya kodi yaroqsiz yoki muddati tugagan.',
  server_error: 'Shaffof ID serverida xatolik.',
}

function errorMessage(code: string): string {
  return MESSAGES[code] ?? `Kirishda xatolik: ${code}`
}

export { createShaffofIdButton } from './vanilla'
export { openSigningFrame } from './sign'
export type { SignOptions, SignResult } from './sign'
export { createIdentityCard, createReceiptCard, createScopeList } from './ui'
export type { IdentityInfo, ReceiptInfo } from './ui'
export { scopeLabel } from './labels'
